"use client";

import React, { useEffect, useState } from "react";
import { Game } from '@/domain/general.interface';
import { GeneralEndpoints } from "@/graph/general.endpoints";
import Select from "@apg.gg/core/lib/Select";
import { useInfiniteQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import Image from 'next/image';
import { useProfileTypes } from '@/hooks/useGeneral';
import { CLASIFICATIONS_OPTIONS } from '@/lib/utils/constants';
import InfiniteScroll from '@/components/InfiniteScroll';
import { UserGame } from '@/domain/account/profile.interface';
import CardProfileGame from '@/components/ui/CardProfileGame';

interface Props {
  game: Game;
  slug: string;
  userId: number;
}

export default function GameDetailComponent({
  game,
  slug,
  userId
}: Props) {
  const t = useTranslations('Game');
  const { data: profileTypes } = useProfileTypes();
  const [filters, setFilters] = useState({
    slug: slug as string,
    profileTypes: [],
    clasifications: [],
    limit: 12
  });

  const types = profileTypes?.map(profileType => ({
    value: String(profileType?.slug),
    label: profileType?.name || '',
  }))

  const {
    status,
    data,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    refetch
  } = useInfiniteQuery({
    queryKey: ['apg-users-game', slug],
    queryFn: async ({ pageParam }) => await GeneralEndpoints.getUsersByGameSlug({
      ...filters,
      page: pageParam,
      userLoggedId: userId
    }),
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => firstPage.previousPage ?? undefined,
    getNextPageParam: (lastPage) => lastPage.nextPage ?? undefined,
    refetchOnMount: 'always'
  })

  const handleProfileTypesChange = (values: any) => {
    setFilters(prev => ({
      ...prev,
      profileTypes: values
    }));
  };

  const handleClasificationChange = (values: any) => {
    setFilters(prev => ({
      ...prev,
      clasifications: values
    }));
  };

  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      slug: slug as string
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  return (
    <>
      <section id="game-detail" className="row my-8 space-y-8">
        <div className="container flex flex-col gap-4 md:gap-8">
          {game ? (
            <div className="flex flex-col bg-black-900 px-6 md:px-9 py-6 md:py-8 gap-2 md:gap-6 rounded-2xl items-center">
              <div className="flex items-stretch gap-4 md:gap-12 w-full">
                <div className="flex items-start">
                  <div className="w-16 md:w-36 shadow-2xl rounded-lg md:rounded-2xl overflow-hidden">
                    <Image src={game.thumbnail} alt="" width={300} height={400} />
                  </div>
                </div>
                <div className="flex flex-col justify-start gap-2">
                  <h1 className="text-title-lg md:text-display-md">{game.name}</h1>
                  <div className="flex gap-2">
                    <div className="flex gap-1 text-xs md:text-base">
                      <strong className="font-extrabold">{data?.pages[0].total}</strong>
                      {t('users')}
                    </div>
                  </div>
                  <div className="hidden sm:block max-w-5xl">
                    <p aria-label={game.description} className="text-body-lg">{game.description}</p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="flex flex-col sm:flex-row mb-4 w-full justify-between items-center gap-2">
            <div className="flex justify-start w-full sm:w-64">
              <Select
                placeholder={t("search-by-profile-types")}
                onSelect={handleProfileTypesChange}
                options={types || []}
                isSearchable
                clearable
                onClear={() => handleProfileTypesChange([])}
                className="w-full sm:w-64"
              />
            </div>
            <div className="flex justify-end w-full sm:w-48">
              <Select
                placeholder='All'
                value={CLASIFICATIONS_OPTIONS[0].value}
                onSelect={handleClasificationChange}
                options={CLASIFICATIONS_OPTIONS}
                className="w-full sm:w-48"
              />
            </div>
          </div>
          <AnimatePresence>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <InfiniteScroll
                data={data}
                fetchFunction={fetchNextPage}
                status={status}
                isFetchingNextPage={isFetchingNextPage}
                hasNextPage={hasNextPage}
                error={error as Error}
                renderItem={(user: UserGame) => (
                  <CardProfileGame
                    user={user}
                    game={game}
                  />
                )}
              />
            </div>

            {data?.pages?.length === 0 && (
              <motion.div
                className="flex flex-col bg-black-900 px-9 py-8 gap-6 rounded-2xl items-center"
                layout
                initial={{ transform: "scale(0)" }}
                animate={{ transform: "scale(1)" }}
                exit={{ transform: "scale(0)" }}
              >
                <h1 className="text-2xl font-bold">{t('no-user-added-this-game-to-their-profile')}</h1>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    </>
  )
}